import { toPercent } from './scale';
import { formatNumber } from '@/shared/lib/format';
import { cn } from '@/shared/lib/utils';

export interface DensityBucket {
  key: string;
  /** Rótulo curto do eixo — a data de início da semana, `08/09`. */
  label: string;
  /** A leitura por extenso, para o `title` e o leitor de tela. */
  description: string;
  count: number;
  /** Para onde o clique leva. Sem isto, a coluna não é clicável. */
  to?: string;
}

interface DensityStripProps {
  buckets: readonly DensityBucket[];
  onSelect?: (bucket: DensityBucket) => void;
  className?: string;
}

/**
 * Quantos compromissos a igreja tem em cada semana do período à frente.
 *
 * É a resposta ao pedido literal da US-12 — *"enxergar concentrações de
 * atividade e períodos vazios antes de aprovar coisas novas"* — que nenhuma tela
 * dava, porque o calendário mostra um mês por vez e a concentração só aparece
 * comparando semanas lado a lado.
 *
 * ⚠️ **MONOCROMÁTICA, e isto é regra de acesso, não escolha estética.** A faixa
 * é alimentada pela RPC `agenda_projects`, que serve a filial inteira aos cinco
 * papéis e **colapsa `changes_requested` em `under_review`** para quem não é
 * dono do projeto ([ADR-015](../../../../docs/adr/ADR-015-agenda-volatil.md)
 * §4). Contar o que a grade já mostra àquela pessoa é estritamente menos
 * informação do que a grade — seguro. Empilhar por situação seria desenhar
 * distinções sobre projeto alheio, bem no lugar onde o servidor acabou de
 * apagá-las.
 *
 * Por isso a cor é `primary`, e não um tom de situação: a altura aqui significa
 * **quantidade**, não firmeza, e vestir o verde de "aprovado" faria a faixa
 * prometer uma leitura que ela não entrega.
 */
export function DensityStrip({ buckets, onSelect, className }: DensityStripProps) {
  const max = Math.max(...buckets.map((bucket) => bucket.count), 0);
  const busiest = max > 0 ? buckets.find((bucket) => bucket.count === max) : undefined;

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-end gap-1">
        {buckets.map((bucket) => {
          const isBusiest = busiest?.key === bucket.key && max > 1;
          const column = (
            <>
              <div
                aria-hidden="true"
                className="flex h-16 w-full flex-col justify-end overflow-hidden rounded-md bg-muted/60"
              >
                <div
                  className={cn(
                    'w-full rounded-md transition-[height] duration-fast ease-out',
                    isBusiest ? 'bg-primary' : 'bg-primary/45',
                  )}
                  style={{ height: `${toPercent(bucket.count, max)}%` }}
                />
              </div>
              {/* 10px e sem padding lateral: em 375px são oito colunas de ~40px,
                  e "23/08" tem de caber inteiro — truncado para "23/..." o
                  rótulo deixa de dizer qual semana é. */}
              <span className="w-full text-center text-[0.625rem] leading-tight text-muted-foreground">
                {bucket.label}
              </span>
            </>
          );

          // Sem `onSelect`, a faixa é leitura — e então não vira botão. Um
          // controle que não faz nada ao ser acionado é pior que texto.
          return onSelect ? (
            <button
              key={bucket.key}
              type="button"
              onClick={() => onSelect(bucket)}
              title={bucket.description}
              className="flex min-w-0 flex-1 flex-col items-center gap-1 rounded-md px-0 py-0.5 transition-colors duration-fast ease-out hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
            >
              <span className="sr-only">{bucket.description}</span>
              {column}
            </button>
          ) : (
            <div
              key={bucket.key}
              title={bucket.description}
              className="flex min-w-0 flex-1 flex-col items-center gap-1 px-0 py-0.5"
            >
              <span className="sr-only">{bucket.description}</span>
              {column}
            </div>
          );
        })}
      </div>

      {/*
        ⚠️ **Cor nunca é o único portador** (US-14). A coluna mais alta é dita
        por extenso aqui — quem não distingue as duas intensidades de azul lê a
        mesma conclusão na frase.

        Com `max <= 1` a frase não sai: "a semana mais cheia tem 1" não é uma
        concentração, é ruído com cara de descoberta.
      */}
      {busiest && max > 1 ? (
        <p className="text-sm text-muted-foreground">
          A semana de <span className="font-medium text-foreground">{busiest.label}</span> é a
          mais cheia: <span className="tabular font-medium text-foreground">{formatNumber(max)}</span>{' '}
          {max === 1 ? 'compromisso' : 'compromissos'}.
        </p>
      ) : null}
    </div>
  );
}
